// Is the local read store serving this session's reads? (#207 U4)
//
// **It is, by default.** `DEFAULT_ON` is `true`, so a teacher with nothing set
// reads from `appStore` with the queue folded over it. The flag is the
// rollback: `localStorage['coda.readStore'] = 'off'` puts one browser back on
// `onSnapshot` without a deploy.
//
// ── The rollback and lite dispatch (#410) ─────────────────────────────────
//
// Lite dispatch writes over REST and enters no mutation queue, so
// `hasLocalMutations` is never true. The store arm's optimistic read comes from
// *our* queue through `overlay.js`; the snapshot arm has no overlay, and its
// row and `pendingWrite` are `d.data()` and `d.metadata.hasPendingWrites`
// straight off the listener. A save offline on that arm under lite would close
// the drawer on an unchanged row (#237).
//
// So `src/data/liteWriteFlag.js` reads *this* flag and refuses lite writes
// whenever it says `'off'`: the rollback takes the write path back with it, in
// the same reload, and a batch already queued under lite is replayed through
// the full SDK, entering the mutation queue. Setting `coda.readStore` to
// `'off'` is the whole of the rollback. `test/ui/rollbackArmPendingWrite.test.jsx`
// drives both arms over one queued, unlanded rename and holds each to its
// supplier.
//
// The `onSnapshot` listener stays where it is, and the flag decides which of
// the two answers the provider renders. Both arms are run — see
// `scripts/test-arms.mjs`.
//
// ── Why `localStorage` and not `import.meta.env` ──────────────────────────
//
// `import.meta.env` is a build-time substitution and a **parse error** under
// jest, which is why `receiptOcr.js`, `reportPdf.js` and
// `useExpenseEntryForm.js` keep it out of any module a test loads (#112).
// `localStorage` can be flipped in a browser console on a deployed build:
//
//     localStorage.setItem('coda.readStore', 'on');   // then reload
//     localStorage.removeItem('coda.readStore');      // then reload
//
// ── Read once per mount, never per render ────────────────────────────────
//
// Callers capture this in a `useState` initialiser. A value that changed
// between renders would change which of two subscriptions a provider is holding
// while it holds one of them; a reload switches.

const KEY = 'coda.readStore';

/**
 * A test's answer, when it has one. `null` means "ask the browser".
 *
 * An override rather than a `localStorage` write because jsdom's storage is
 * shared across a file's tests and a leaked `setItem` would be a flag one test
 * set and another inherited.
 */
let override = null;

/** Force the answer. `null` hands it back to `localStorage`. Tests, and only tests. */
export function __setReadStoreEnabled(value) {
  override = value;
}

/**
 * The default, as one named constant.
 *
 * Lite dispatch (#410) is a separate flag, `coda.liteWrites`, off by default.
 * This flag's `'off'` governs the write side (see the header); otherwise
 * flipping it changes where reads come from and nothing about where writes go.
 *
 * ── Rolling back ──────────────────────────────────────────────────────────
 *
 * One browser: `localStorage.setItem('coda.readStore', 'off')` and reload.
 * Everybody: this constant back to `false`, plus `readStoreFlag.test.jsx`'s
 * pins of it, in one commit. Nothing else in the pure suites depends on it, and
 * `scripts/test-arms.mjs` follows the constant without being edited — the two
 * passes swap roles and both still run.
 *
 * ── It is exported, and `scripts/test-arms.mjs` is why ────────────────────
 *
 * `npm test` runs both arms (#281), and it takes them from this constant. Arms
 * spelled as "the environment unset" and "`CODA_READ_STORE=on`" are two
 * *different* arms only while the default is `false`; with it `true` both
 * passes would be the store arm and the snapshot arm would go unrun, with the
 * summary still naming it. See that file's header.
 */
export const DEFAULT_ON = true;

// The environment's switch, for jest: it has no `localStorage` worth setting
// from outside the process, so under Node this reads `process.env`, and one
// command runs every suite on the other arm without editing a file. Not
// `import.meta.env`, for the reason the header gives (#112).
const ENV_KEY = 'CODA_READ_STORE';

/**
 * Is the read store on?
 *
 * **Every failure to find out is the default**, which is on: a browser
 * refusing storage, a server-side render, a jsdom without a `window`. What the
 * store does when IndexedDB itself is refused is #208, answered in
 * `idbStore.js` as `memory-degraded`.
 *
 * Precedence, most specific first: a test's override, the environment, the
 * browser's `localStorage`, the default.
 */
export function readStoreEnabled() {
  if (override !== null) return override;
  try {
    const env = globalThis.process?.env?.[ENV_KEY];
    if (env === 'on') return true;
    if (env === 'off') return false;
  } catch {
    // No `process`, or one that refuses `env`. Fall through to the browser.
  }
  try {
    const stored = globalThis.localStorage?.getItem(KEY);
    if (stored === 'on') return true;
    if (stored === 'off') return false;
  } catch {
    // Private mode, or a browser configured to refuse site data: `getItem`
    // itself throws. Not an error worth reporting — it is an answer.
    return DEFAULT_ON;
  }
  return DEFAULT_ON;
}
